import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import toNumber from 'lodash/toNumber';
import unionBy from 'lodash/unionBy';

import { isFixedChargeLvoForUnit, isProvisionedChargeLvoForUnit, type LvoLike } from '../chargesUtils';
import { Colors } from '../colorUtils';
import { cleanInputCreate } from '../databaseUtils';
import {
  CalendarDate,
  addCalendarDate,
  endOfCalendarMonth,
  setCalendarDate,
  startOfCalendarMonth,
  subtractCalendarDate,
  today,
  toCalendarDate,
  toCalendarDateNullable,
} from '../dateutils';
import { DEFAULT_LANGUAGE } from '../emailFixedTemplatesUtils';
import { EXACT_ONLINE_UNKNOWN_ACCOUNT_MATCHING_FOR_CUSTOM_VALUE } from '../integrations';
import { getLeaseIndexationForDate } from '../leaseutils';
import { AccountLabel, Language, Posting, Transaction, TransactionLinkType, UnitLease, VatRate } from '../types';
import {
  AccountLabelCustomUsage,
  CreatePostingInput,
  FileCategory,
  InvoiceType,
  LeasePaymentInvoicePeriod,
  PostingStatus,
  PostingType,
} from '../types/dto-app';

import {
  ACCOUNT_ACP_FUNDS_TOPCLASS,
  ACCOUNT_CHARGES_LOSS_ON_TENANT_RECEIVABLE_CLASS,
  ACCOUNT_CHARGES_TOPCLASS,
  ACCOUNT_ACP_WORKING_CAPITAL_CLASS,
  ACCOUNT_LEASE_TOPCLASS,
  ACCOUNT_OWNERS_TOPCLASS,
  ACCOUNT_PAYMENT_PROVIDER_FEE_CLASS,
  ACCOUNT_PAYMENT_PROVIDER_TRANSIT_CLASS,
  ACCOUNT_PAYMENT_PROVIDER_TRANSIT_TOPCLASS,
  ACCOUNT_RENTAL_GUARANTIES_RECEIVED_CLASS,
  ACCOUNT_RENTAL_GUARANTIES_RECEIVED_TOPCLASS,
  ACCOUNT_SUSPENSE_CHARGES_PAID_IN_THE_PAST_CLASS,
  ACCOUNT_SUSPENSE_RENTAL_GUARANTIES_RECEIVED_CLASS,
  ACCOUNT_SUSPENSE_TOPCLASS,
  ACCOUNT_SUSPENSE_TURNOVER_CHARGES_ADJUSTEMENT_CLASS,
  ACCOUNT_SUSPENSE_TURNOVER_EXCEPTIONAL_REVENUE_CLASS,
  ACCOUNT_SUSPENSE_TURNOVER_FIXED_CHARGES_CLASS,
  ACCOUNT_SUSPENSE_TURNOVER_FURNISHED_RENT_CLASS,
  ACCOUNT_SUSPENSE_TURNOVER_PROVISIONED_CHARGES_CLASS,
  ACCOUNT_SUSPENSE_TURNOVER_RENT_CLASS,
  ACCOUNT_SUSPENSE_TURNOVER_TERMINATION_INDEMNITY,
  ACCOUNT_SUSPENSE_TURNOVER_VARIOUS_OPERATIONS_CHARGE_CLASS,
  ACCOUNT_SUSPENSE_TURNOVER_VARIOUS_OPERATIONS_SERVICE_CLASS,
  ACCOUNT_TURNOVER_CHARGES_ADJUSTEMENT_CLASS,
  ACCOUNT_TURNOVER_CUSTOM_LINE_CLASS,
  ACCOUNT_TURNOVER_EXCEPTIONAL_REVENUE_CLASS,
  ACCOUNT_TURNOVER_FIXED_CHARGES_CLASS,
  ACCOUNT_TURNOVER_FURNISHED_RENT_CLASS,
  ACCOUNT_TURNOVER_LETTING_FEE_CLASS,
  ACCOUNT_TURNOVER_MANAGEMENT_FEES_CLASS,
  ACCOUNT_TURNOVER_PROVISIONED_CHARGES_CLASS,
  ACCOUNT_TURNOVER_RENT_CLASS,
  ACCOUNT_TURNOVER_RENT_GUARANTEE_INSURANCE_CLASS,
  ACCOUNT_TURNOVER_SALES_COMMISSION_CLASS,
  ACCOUNT_TURNOVER_SERVICE_CLEANING_CLASS,
  ACCOUNT_TURNOVER_SERVICES_TOPCLASS,
  ACCOUNT_TURNOVER_TERMINATION_INDEMNITY,
  ACCOUNT_TURNOVER_TOPCLASS,
  ACCOUNT_TURNOVER_VARIOUS_OPERATIONS_CHARGE_CLASS,
  ACCOUNT_TURNOVER_VARIOUS_OPERATIONS_SERVICE_CLASS,
  getMirroredAccountClassFor,
} from './accountClasses';
import { PostingForReconciliation } from './reconciliationUtils';

export interface FinancialInformationsFromTransactions {
  rentReceived: number;
  chargesReceived: number;
  claimOfTenants: { leaseId: string; amount: number }[];
  turnover: number;
  totalPaymentTimes: number;
  totalLeases: number;
}

export enum LinkType {
  // eslint-disable-next-line @typescript-eslint/no-shadow
  Lease = 'Lease',
  Contractor = 'Contractor',
  Owner = 'Owner',
}

export type LinkTo =
  | { type: LinkType.Lease; leaseId: string; unitId?: string; amount: number }
  | { type: LinkType.Contractor; id: string; amount: number }
  | { type: LinkType.Owner; id: string; amount: number };

export const DEFAULT_VAT_RATES: Pick<VatRate, 'country' | 'label' | 'value' | 'ownerId' | 'customValues'>[] = [
  { country: 'BE', label: 'accounting.vat.exemption', value: null, customValues: [] },
  { country: 'BE', label: 'accounting.vat.intracommunity', value: 0, customValues: [] },
  { country: 'BE', label: '0 %', value: 0, customValues: [] },
  { country: 'BE', label: '6 %', value: 6, customValues: [] },
  { country: 'BE', label: '12 %', value: 12, customValues: [] },
  { country: 'BE', label: '21 %', value: 21, customValues: [] },
];

export const DEFAULT_AGENCY_RATE_VAT_VALUE = 21;
export const DEFAULT_VAT_RATE = 21;

/**
 * Get the leaseId from postings
 *
 * @param postings
 * @returns {string} leaseId or null if none
 */
export const getLeaseIdFromPostings = (postings: Posting[]) => {
  if (isNil(postings) || isEmpty(postings)) {
    return null;
  }

  const posting = postings.find((p) => !isNil(p.lease) && !isNil(p.lease.id));

  if (isNil(posting)) {
    return null;
  }

  return posting.lease!.id;
};

/**
 * Get the unitId from postings
 *
 * @param postings
 * @returns {string} unitId or null if none
 */
export const getUnitIdFromPostings = (postings: Posting[]) => {
  if (isNil(postings) || isEmpty(postings)) {
    return null;
  }

  const posting = postings.find((p) => !isNil(p.unit) && !isNil(p.unit.id));

  if (isNil(posting)) {
    return null;
  }

  return posting.unit!.id;
};

export const getTransactionAmountReconciledInLeaseOrContractor = (transaction: Transaction, linkId: string) =>
  transaction.postings?.reduce((sum, posting) => {
    if ([posting.leaseId, posting.contactId].includes(linkId) && posting.invoicePostingId) {
      if (posting.type === PostingType.CREDIT) {
        sum += posting.totalAmount;
      } else if (posting.leaseVariousOperationId) {
        sum -= posting.totalAmount;
      }
    }
    return sum;
  }, 0) ?? 0;

export const getTransactionAmountLinkWithLease = (transaction: Transaction, leaseId: string) =>
  transaction.links?.reduce((sum, link) => {
    if (link.linkType === TransactionLinkType.LEASE && leaseId === link.linkId) {
      sum += link.amount;
    }
    return sum;
  }, 0) ?? 0;

export const getTransactionUnlinkedAmountForLease = (transaction: Transaction, leaseId: string) => {
  const amountReconciledWithCurrentLease = getTransactionAmountReconciledInLeaseOrContractor(transaction, leaseId);
  const amountLinkedToCurrentLease = getTransactionAmountLinkWithLease(transaction, leaseId);
  const amountUnlinked = amountLinkedToCurrentLease - amountReconciledWithCurrentLease;
  return amountUnlinked;
};

export const getAmountColor = (amount: number) => {
  if (amount > 0) {
    return Colors.TURQUOISE_BLUE;
  }
  if (amount < 0) {
    return Colors.CARNATION_RED;
  }
  return Colors.SILVER;
};

/**
 * Get the contractorId from postings
 *
 * @param postings
 * @returns {string} contractorId or null if none
 */
export const getContractorIdFromPostings = (postings: Posting[]) => {
  if (isNil(postings) || isEmpty(postings)) {
    return null;
  }

  const posting = postings.find((p) => !isNil(p.contact) && !isNil(p.contact.id));

  if (isNil(posting)) {
    return null;
  }

  return posting.contact!.id;
};

/**
 * Get some financial informations about the owner
 * @param transactions The list of transactions
 */
export const getFinancialInformationsFromTransactions = (
  transactions?: Transaction[]
): FinancialInformationsFromTransactions => {
  const defaultValues = {
    rentReceived: 0,
    chargesReceived: 0,
    claimOfTenants: [],
    turnover: 0,
    totalPaymentTimes: 0,
    totalLeases: 0,
  };
  if (isNil(transactions)) {
    return defaultValues;
  }
  const postingInfos: FinancialInformationsFromTransactions = transactions.reduce(
    (currentResults, currentTransaction) => {
      // Get the right posting
      const currentPosting = currentTransaction.postings!.find((p) => p.lease && !isNil(p.lease.id));
      if (!currentPosting || !currentPosting.lease) {
        return currentResults;
      }
      const totalAmount = currentPosting.totalAmount * -1.0;
      let currentRentalPrice = currentPosting.lease.totalRentalPrice;
      if (!isNil(currentPosting.lease.leasePriceHistories) && !isEmpty(currentPosting.lease.leasePriceHistories)) {
        const leaseIndexation = getLeaseIndexationForDate(
          toCalendarDateNullable(currentTransaction.operationDate),
          currentPosting.lease.leasePriceHistories
        );
        if (!isNil(leaseIndexation)) {
          currentRentalPrice = leaseIndexation.totalRentalPrice ?? 0;
        } else {
          // No indexation found, take initialPrice
          currentRentalPrice = currentPosting.lease.totalInitialRentalPrice;
        }
      }

      // Value are in - check to put in positive and that it can go other way if error.
      // Check the shake of the owner to multiply by it....
      if (totalAmount >= currentRentalPrice) {
        currentResults.rentReceived += currentRentalPrice;
        currentResults.chargesReceived += totalAmount - currentRentalPrice;
        const payementDate = currentTransaction.operationDate;
        if (!isNil(payementDate)) {
          let leasePaymentDate: CalendarDate = today();
          if (currentPosting.lease.paymentInvoicePeriod === LeasePaymentInvoicePeriod.CUSTOM_PERIOD) {
            leasePaymentDate = setCalendarDate(
              toCalendarDate(payementDate),
              toNumber(currentPosting.lease.paymentInvoicePeriodCustomDay),
              'day'
            );
          } else if (currentPosting.lease.paymentInvoicePeriod === LeasePaymentInvoicePeriod.CONTRACT_PERIOD) {
            const startDay = toCalendarDate(currentPosting.lease.startDate).day;
            leasePaymentDate = setCalendarDate(toCalendarDate(payementDate), startDay, 'day');
            const endOfPaymentMonth = endOfCalendarMonth(leasePaymentDate);
            const endOfLastMonth = endOfCalendarMonth(subtractCalendarDate(endOfPaymentMonth, 1, 'months'));
            const endOfNextMonth = endOfCalendarMonth(addCalendarDate(endOfPaymentMonth, 1, 'months'));
            leasePaymentDate = [endOfLastMonth, endOfPaymentMonth, endOfNextMonth].reduce((closest, candidate) =>
              Math.abs(leasePaymentDate.until(candidate, { largestUnit: 'days' }).days) <
              Math.abs(leasePaymentDate.until(closest, { largestUnit: 'days' }).days)
                ? candidate
                : closest
            );
          } else if (currentPosting.lease.paymentInvoicePeriod === LeasePaymentInvoicePeriod.CALENDAR_PERIOD) {
            leasePaymentDate = toCalendarDate(payementDate);
            const startOfPaymentMonth = startOfCalendarMonth(leasePaymentDate);
            const startOfLastMonth = startOfCalendarMonth(subtractCalendarDate(startOfPaymentMonth, 1, 'months'));
            const startOfNextMonth = startOfCalendarMonth(addCalendarDate(startOfPaymentMonth, 1, 'months'));
            leasePaymentDate = [startOfLastMonth, startOfPaymentMonth, startOfNextMonth].reduce((closest, candidate) =>
              Math.abs(leasePaymentDate.until(candidate, { largestUnit: 'days' }).days) <
              Math.abs(leasePaymentDate.until(closest, { largestUnit: 'days' }).days)
                ? candidate
                : closest
            );
          }
          currentResults.totalPaymentTimes += leasePaymentDate.until(toCalendarDate(payementDate), {
            largestUnit: 'days',
          }).days;
          currentResults.totalLeases = currentResults.totalLeases + 1;
        }
      } else {
        currentResults.chargesReceived += totalAmount;
      }

      //@ts-expect-error typing
      currentResults.claimOfTenants = unionBy(
        currentResults.claimOfTenants,
        [{ leaseId: currentPosting.lease.id, amount: currentPosting.lease.balance }],
        'leaseId'
      );
      currentResults.turnover += totalAmount;
      return currentResults;
    },
    defaultValues
  );

  return postingInfos;
};

export const getFromPartAccountOfTransaction = (transaction: Transaction) => {
  if (transaction.amount < 0) {
    return transaction.bankAccount?.number || '';
  }
  return transaction.counterpartAccountNumber || '';
};

export const getFromPartBankNameOfTransaction = (transaction: Transaction) =>
  transaction.amount < 0 ? (transaction.bankAccount?.bankName ?? '') : '';

export const getToPartAccountOfTransaction = (transaction: Transaction) => {
  if (transaction.amount > 0) {
    return transaction.bankAccount?.number || '';
  }
  return transaction.counterpartAccountNumber || '';
};

export const isPostingReversalOrCancelledByReversal = (posting: Pick<Posting, 'status'>) =>
  !isNil(posting.status) && [PostingStatus.CANCELLED_BY_REVERSAL, PostingStatus.REVERSAL].includes(posting.status);

/**
 * A settlement linked to a bank transaction or to an invoice line, or a reversal: it keeps the line period of the day
 * it was recorded.
 */
export const isReconciliationPosting = (posting: Pick<Posting, 'status' | 'transactionId' | 'invoicePostingId'>) =>
  !isNil(posting.transactionId) || !isNil(posting.invoicePostingId) || isPostingReversalOrCancelledByReversal(posting);

export const getToPartBankNameOfTransaction = (transaction: Transaction) =>
  transaction.amount > 0 ? (transaction.bankAccount?.bankName ?? '') : '';

export const getCorrectLabel = (
  labels: {
    language: string;
    label: string;
  }[],
  language: string
) => {
  return (
    labels.find((label) => label.language.toLocaleLowerCase() === language.toLocaleLowerCase()) ??
    labels.find((label) => label.language.toLocaleLowerCase() === DEFAULT_LANGUAGE.toLocaleLowerCase())
  );
};

export const getOppositePostingType = (postingType: PostingType) =>
  postingType === PostingType.CREDIT ? PostingType.DEBIT : PostingType.CREDIT;

export const getReversalCreatePostingInput = (posting: Posting) => {
  const newCustomValues =
    posting.customValues?.filter(
      (customValue) => customValue.name === EXACT_ONLINE_UNKNOWN_ACCOUNT_MATCHING_FOR_CUSTOM_VALUE
    ) ?? [];
  const { id: _id, ...restPosting } = posting;
  const strippedPosting = Object.fromEntries(
    Object.entries(restPosting).filter(
      ([, value]) => value === null || typeof value !== 'object' || Array.isArray(value)
    )
  );
  const cleanInput = cleanInputCreate({
    ...strippedPosting,
    customValues: newCustomValues,
    type: getOppositePostingType(posting.type),
  }) as CreatePostingInput;
  return cleanInput;
};

export const getReconciliationPostingType = (posting: PostingForReconciliation) => {
  if (posting.invoice?.type === InvoiceType.VARIOUS_OPERATION_GUARANTY_RELEASE) {
    return Boolean(posting.invoice?.creditNote) ? PostingType.DEBIT : PostingType.CREDIT;
  }
  return posting.type;
};

export const filterAccountLabelsForSpecificUnitLease = (
  accountLabels: AccountLabel[],
  unitLease: Pick<UnitLease, 'indexation' | 'furnishedRental' | 'unitId'>,
  leaseVariousOperations: LvoLike[] = []
) =>
  accountLabels.filter((accountLabel) => {
    if (accountLabel.topClass !== ACCOUNT_TURNOVER_TOPCLASS) {
      return false;
    }
    if (
      !unitLease.indexation &&
      accountLabel.class === ACCOUNT_TURNOVER_RENT_CLASS &&
      accountLabel.customUsage === AccountLabelCustomUsage.INDEXATION_REGULARIZATION
    ) {
      return false;
    }

    if (!unitLease.furnishedRental && accountLabel.class === ACCOUNT_TURNOVER_FURNISHED_RENT_CLASS) {
      return false;
    }

    const hasProvisionedCharge = leaseVariousOperations.some((lvo) =>
      isProvisionedChargeLvoForUnit(lvo, unitLease.unitId)
    );
    if (
      !hasProvisionedCharge &&
      (accountLabel.class === ACCOUNT_TURNOVER_PROVISIONED_CHARGES_CLASS ||
        accountLabel.class === ACCOUNT_TURNOVER_CHARGES_ADJUSTEMENT_CLASS)
    ) {
      return false;
    }

    const hasFixedCharge = leaseVariousOperations.some((lvo) => isFixedChargeLvoForUnit(lvo, unitLease.unitId));
    if (!hasFixedCharge && accountLabel.class === ACCOUNT_TURNOVER_FIXED_CHARGES_CLASS) {
      return false;
    }

    return true;
  });

interface DefaultAccountLabel {
  id: string;
  class: number;
  topClass: number;
  labels: { language: Language; label: string }[];
  customUsage?: AccountLabelCustomUsage;
}

const DEFAULT_LEDGER_ACCOUNT_LABELS: DefaultAccountLabel[] = [
  {
    id: `DefaultAccountLabel1`,
    class: ACCOUNT_LEASE_TOPCLASS,
    topClass: ACCOUNT_LEASE_TOPCLASS,
    labels: [
      { language: Language.FR, label: 'Créances clients' },
      { language: Language.EN, label: 'Accounts receivables' },
      { language: Language.NL, label: 'Handelsvorderingen' },
      { language: Language.DE, label: 'Forderungen an Kunden' },
    ],
  },
  {
    id: `DefaultAccountLabel2`,
    class: 610000,
    topClass: ACCOUNT_CHARGES_TOPCLASS,
    labels: [
      { language: Language.FR, label: 'Protection incendie' },
      { language: Language.EN, label: 'Fire protection' },
      { language: Language.NL, label: 'Brandbeveiliging' },
      { language: Language.DE, label: 'Brandschutz' },
    ],
  },
  {
    id: `DefaultAccountLabel3`,
    class: 610100,
    topClass: ACCOUNT_CHARGES_TOPCLASS,
    labels: [
      { language: Language.FR, label: 'Contôle ascenseurs' },
      { language: Language.EN, label: 'Lift control' },
      { language: Language.NL, label: 'Controle van liften' },
      { language: Language.DE, label: 'Aufzugsprüfung' },
    ],
  },
  {
    id: `DefaultAccountLabel4`,
    class: 610120,
    topClass: ACCOUNT_CHARGES_TOPCLASS,
    labels: [
      { language: Language.FR, label: "Contrat d'entretien ascenseur" },
      { language: Language.EN, label: 'Lift maintenance contract' },
      { language: Language.NL, label: 'Onderhoudscontract voor liften' },
      { language: Language.DE, label: 'Wartungsvertrag für Aufzüge' },
    ],
  },
  {
    id: `DefaultAccountLabel5`,
    class: 610200,
    topClass: ACCOUNT_CHARGES_TOPCLASS,
    labels: [
      { language: Language.FR, label: "Signalitique et éclairage d'urgence" },
      { language: Language.EN, label: 'Emergency signage and lighting' },
      { language: Language.NL, label: 'Signalering en verlichting voor noodgevallen' },
      { language: Language.DE, label: 'Notbeleuchtung und -beschilderung' },
    ],
  },
  {
    id: `DefaultAccountLabel6`,
    class: 610300,
    topClass: ACCOUNT_CHARGES_TOPCLASS,
    labels: [
      { language: Language.FR, label: 'Sanitaires et chauffage' },
      { language: Language.EN, label: 'Sanitary and heating' },
      { language: Language.NL, label: 'Sanitair en verwarming' },
      { language: Language.DE, label: 'Sanitär und Heizung' },
    ],
  },
  {
    id: `DefaultAccountLabel7`,
    class: 610500,
    topClass: ACCOUNT_CHARGES_TOPCLASS,
    labels: [
      { language: Language.FR, label: "Nettoyage et traitement d'ordures ménagères" },
      { language: Language.EN, label: 'Cleaning and processing of household waste' },
      { language: Language.NL, label: 'Reiniging en verwerking van huishoudelijk afval' },
      { language: Language.DE, label: 'Reinigung und Entsorgung von Hausmüll' },
    ],
  },
  {
    id: `DefaultAccountLabel8`,
    class: 610600,
    topClass: ACCOUNT_CHARGES_TOPCLASS,
    labels: [
      { language: Language.FR, label: 'Jardins, parkings et chemins' },
      { language: Language.EN, label: 'Gardens, car parks and paths' },
      { language: Language.NL, label: 'Tuinen, parkings en paden' },
      { language: Language.DE, label: 'Gärten, Parkplätze und Wege' },
    ],
  },
  {
    id: `DefaultAccountLabel9`,
    class: 610700,
    topClass: ACCOUNT_CHARGES_TOPCLASS,
    labels: [
      { language: Language.FR, label: 'Entretiens bâtiments' },
      { language: Language.EN, label: 'Building maintenance' },
      { language: Language.NL, label: 'Onderhoud van gebouwen' },
      { language: Language.DE, label: 'Gebäudewartung' },
    ],
  },
  {
    id: `DefaultAccountLabel10`,
    class: 611100,
    topClass: ACCOUNT_CHARGES_TOPCLASS,
    labels: [
      { language: Language.FR, label: 'Travaux de rénovation et autres travaux exceptionnels' },
      { language: Language.EN, label: 'Renovation and other exceptional works' },
      { language: Language.NL, label: 'Renovatie en andere uitzonderlijke werkzaamheden' },
      { language: Language.DE, label: 'Renovierung und andere außergewöhnliche Arbeiten' },
    ],
  },
  {
    id: `DefaultAccountLabel11`,
    class: 612100,
    topClass: ACCOUNT_CHARGES_TOPCLASS,
    labels: [
      { language: Language.FR, label: 'Electricité' },
      { language: Language.EN, label: 'Electricity' },
      { language: Language.NL, label: 'Elektriciteit' },
      { language: Language.DE, label: 'Elektrizität' },
    ],
  },
  {
    id: `DefaultAccountLabel12`,
    class: 612200,
    topClass: ACCOUNT_CHARGES_TOPCLASS,
    labels: [
      { language: Language.FR, label: 'Gaz' },
      { language: Language.EN, label: 'Gas' },
      { language: Language.NL, label: 'Gas' },
      { language: Language.DE, label: 'Gas' },
    ],
  },
  {
    id: `DefaultAccountLabel13`,
    class: 612300,
    topClass: ACCOUNT_CHARGES_TOPCLASS,
    labels: [
      { language: Language.FR, label: 'Eau' },
      { language: Language.EN, label: 'Water' },
      { language: Language.NL, label: 'Water' },
      { language: Language.DE, label: 'Wasser' },
    ],
  },
  {
    id: `DefaultAccountLabel14`,
    class: 612400,
    topClass: ACCOUNT_CHARGES_TOPCLASS,
    labels: [
      { language: Language.FR, label: 'Entretien chaudière' },
      { language: Language.EN, label: 'Boiler maintenance' },
      { language: Language.NL, label: 'Onderhoud van verwarmingsketels' },
      { language: Language.DE, label: 'Kesselwartung' },
    ],
  },
  {
    id: `DefaultAccountLabel15`,
    class: 612410,
    topClass: ACCOUNT_CHARGES_TOPCLASS,
    labels: [
      { language: Language.FR, label: 'Entretien caméra, alarme, vidéophone' },
      { language: Language.EN, label: 'Camera, alarm and videophone maintenance' },
      { language: Language.NL, label: "Onderhoud van camera's, alarmen en videofoons" },
      { language: Language.DE, label: 'Wartung von Kameras, Alarmen und Videosprechanlagen' },
    ],
  },
  {
    id: `DefaultAccountLabel16`,
    class: 612500,
    topClass: ACCOUNT_CHARGES_TOPCLASS,
    labels: [
      { language: Language.FR, label: 'Autres fournitures' },
      { language: Language.EN, label: 'Other supplies' },
      { language: Language.NL, label: 'Andere benodigdheden' },
      { language: Language.DE, label: 'Andere Versorgungsleistungen' },
    ],
  },
  {
    id: `DefaultAccountLabel17`,
    class: 613000,
    topClass: ACCOUNT_CHARGES_TOPCLASS,
    labels: [
      { language: Language.FR, label: 'Honoraires gestionnaire' },
      { language: Language.EN, label: 'Management fees' },
      { language: Language.NL, label: 'Beheersvergoedingen' },
      { language: Language.DE, label: 'Verwaltungsgebühren' },
    ],
  },
  {
    id: `DefaultAccountLabel18`,
    class: 613010,
    topClass: ACCOUNT_CHARGES_TOPCLASS,
    labels: [
      { language: Language.FR, label: 'Honoraires syndic' },
      { language: Language.EN, label: 'Trustee fees' },
      { language: Language.NL, label: 'Vergoedingen van syndici' },
      { language: Language.DE, label: 'Verwalterhonorare' },
    ],
  },
  {
    id: `DefaultAccountLabel19`,
    class: 613020,
    topClass: ACCOUNT_CHARGES_TOPCLASS,
    labels: [
      { language: Language.FR, label: 'Honoraires avocat' },
      { language: Language.EN, label: "Lawyer's fees" },
      { language: Language.NL, label: 'Advocaatkosten' },
      { language: Language.DE, label: 'Anwaltskosten' },
    ],
  },
  {
    id: `DefaultAccountLabel20`,
    class: 614000,
    topClass: ACCOUNT_CHARGES_TOPCLASS,
    labels: [
      { language: Language.FR, label: 'Assurances' },
      { language: Language.EN, label: 'Insurances' },
      { language: Language.NL, label: 'Verzekeringen' },
      { language: Language.DE, label: 'Versicherungen' },
    ],
  },
  {
    id: `DefaultAccountLabel21`,
    class: 640400,
    topClass: 640000,
    labels: [
      { language: Language.FR, label: 'Précompte immobilier' },
      { language: Language.EN, label: 'Withholding tax on real estate' },
      { language: Language.NL, label: 'Onroerende voorheffing' },
      { language: Language.DE, label: 'Grundsteuer' },
    ],
  },
  {
    id: `DefaultAccountLabel22`,
    class: 640600,
    topClass: 640000,
    labels: [
      { language: Language.FR, label: 'Impôts provinciaux et communaux' },
      { language: Language.EN, label: 'Provincial and municipal taxes' },
      { language: Language.NL, label: 'Provinciale en gemeentelijke belastingen' },
      { language: Language.DE, label: 'Provinz- und Gemeindesteuern' },
    ],
  },
  {
    id: `DefaultAccountLabel23`,
    class: 650000,
    topClass: 650000,
    labels: [
      { language: Language.FR, label: 'Frais bancaires' },
      { language: Language.EN, label: 'Bank charges' },
      { language: Language.NL, label: 'Bankkosten' },
      { language: Language.DE, label: 'Bankgebühren' },
    ],
  },
  {
    id: `DefaultAccountLabel25`,
    class: ACCOUNT_TURNOVER_RENT_CLASS,
    topClass: ACCOUNT_TURNOVER_TOPCLASS,
    labels: [
      { language: Language.FR, label: 'Loyer' },
      { language: Language.EN, label: 'Rent' },
      { language: Language.NL, label: 'Huur' },
      { language: Language.DE, label: 'Miete' },
    ],
  },
  {
    id: `DefaultAccountLabel25_Indexation`,
    class: ACCOUNT_TURNOVER_RENT_CLASS,
    topClass: ACCOUNT_TURNOVER_TOPCLASS,
    labels: [
      { language: Language.FR, label: 'Loyer - Régularisation pour Indexation' },
      { language: Language.EN, label: 'Rent - Adjustment for Indexation' },
      { language: Language.NL, label: 'Huur - Aanpassing voor indexering' },
      { language: Language.DE, label: 'Miete - Anpassung für Indexierung' },
    ],
    customUsage: AccountLabelCustomUsage.INDEXATION_REGULARIZATION,
  },
  {
    id: `DefaultAccountLabel26`,
    class: ACCOUNT_TURNOVER_FURNISHED_RENT_CLASS,
    topClass: ACCOUNT_TURNOVER_TOPCLASS,
    labels: [
      { language: Language.FR, label: 'Loyer mobilier' },
      { language: Language.EN, label: 'Furniture rent' },
      { language: Language.NL, label: 'Huur meubilair' },
      { language: Language.DE, label: 'Möbelmiete' },
    ],
  },
  {
    id: `DefaultAccountLabel28`,
    class: ACCOUNT_TURNOVER_FIXED_CHARGES_CLASS,
    topClass: ACCOUNT_TURNOVER_TOPCLASS,
    labels: [
      { language: Language.FR, label: 'Forfaits charges' },
      { language: Language.EN, label: 'Charge flat-rate' },
      { language: Language.NL, label: 'Forfaitaire lasten' },
      { language: Language.DE, label: 'Pauschalgebühren' },
    ],
  },
  {
    id: `DefaultAccountLabel28_Indexation`,
    class: ACCOUNT_TURNOVER_FIXED_CHARGES_CLASS,
    topClass: ACCOUNT_TURNOVER_TOPCLASS,
    labels: [
      { language: Language.FR, label: 'Forfaits charges - Régularisation pour indexation' },
      { language: Language.EN, label: 'Charge flat-rate - Adjustment for indexation' },
      { language: Language.NL, label: 'Forfaitaire lasten - Aanpassing voor indexering' },
      { language: Language.DE, label: 'Pauschalgebühren - Anpassung für Indexierung' },
    ],
    customUsage: AccountLabelCustomUsage.INDEXATION_REGULARIZATION,
  },
  {
    id: `DefaultAccountLabel30`,
    class: ACCOUNT_TURNOVER_PROVISIONED_CHARGES_CLASS,
    topClass: ACCOUNT_TURNOVER_TOPCLASS,
    labels: [
      { language: Language.FR, label: 'Provision charges' },
      { language: Language.EN, label: 'Provision for charges' },
      { language: Language.NL, label: 'Provisies voor lasten' },
      { language: Language.DE, label: 'Rückstellung für Gebühren' },
    ],
  },
  {
    id: `DefaultAccountLabel31`,
    class: ACCOUNT_TURNOVER_CHARGES_ADJUSTEMENT_CLASS,
    topClass: ACCOUNT_TURNOVER_TOPCLASS,
    labels: [
      { language: Language.FR, label: 'Ajustement charges sur base de décompte' },
      { language: Language.EN, label: 'Adjustment of charges on a statement basis' },
      { language: Language.NL, label: 'Aanpassing lasten op basis van afrekening' },
      { language: Language.DE, label: 'Anpassung der Gebühren auf Abrechnungsbasis' },
    ],
  },
  {
    id: `DefaultAccountLabel32`,
    class: ACCOUNT_TURNOVER_VARIOUS_OPERATIONS_CHARGE_CLASS,
    topClass: ACCOUNT_TURNOVER_TOPCLASS,
    labels: [
      { language: Language.FR, label: 'Opérations diverses (charges)' },
      { language: Language.EN, label: 'Various operations (charges)' },
      { language: Language.NL, label: 'Diverse operaties (lasten)' },
      { language: Language.DE, label: 'Verschiedene Vorgänge (Gebühren)' },
    ],
  },
  {
    id: `DefaultAccountLabel33`,
    class: ACCOUNT_TURNOVER_VARIOUS_OPERATIONS_SERVICE_CLASS,
    topClass: ACCOUNT_TURNOVER_TOPCLASS,
    labels: [
      { language: Language.FR, label: 'Opérations diverses (services)' },
      { language: Language.EN, label: 'Various operations (services)' },
      { language: Language.NL, label: 'Diverse operaties (diensten)' },
      { language: Language.DE, label: 'Verschiedene Vorgänge (Dienstleistungen)' },
    ],
  },
  {
    id: `DefaultAccountLabel34`,
    class: ACCOUNT_TURNOVER_TERMINATION_INDEMNITY,
    topClass: ACCOUNT_TURNOVER_TOPCLASS,
    labels: [
      { language: Language.FR, label: 'Indemnité de résiliation' },
      { language: Language.EN, label: 'Termination indemnity' },
      { language: Language.NL, label: 'Opzeggingsvergoeding' },
      { language: Language.DE, label: 'Kündigungsentschädigung' },
    ],
  },
  {
    id: `DefaultAccountLabel35`,
    class: ACCOUNT_TURNOVER_EXCEPTIONAL_REVENUE_CLASS,
    topClass: ACCOUNT_TURNOVER_TOPCLASS,
    labels: [
      { language: Language.FR, label: 'Revenu exceptionnel' },
      { language: Language.EN, label: 'Exceptional revenue' },
      { language: Language.NL, label: 'Uitzonderlijke opbrengst' },
      { language: Language.DE, label: 'Außerordentliche Einnahmen' },
    ],
  },
  {
    id: `DefaultAccountLabel36`,
    class: ACCOUNT_CHARGES_LOSS_ON_TENANT_RECEIVABLE_CLASS,
    topClass: ACCOUNT_CHARGES_TOPCLASS,
    labels: [
      { language: Language.FR, label: 'Perte sur créance locataire' },
      { language: Language.EN, label: 'Loss on tenant receivable' },
      { language: Language.NL, label: 'Verlies op huurdervordering' },
      { language: Language.DE, label: 'Verlust bei Mieterforderungen' },
    ],
  },
  {
    id: `DefaultAccountLabel37`,
    class: ACCOUNT_RENTAL_GUARANTIES_RECEIVED_CLASS,
    topClass: ACCOUNT_RENTAL_GUARANTIES_RECEIVED_TOPCLASS,
    labels: [
      { language: Language.FR, label: 'Garanties locatives' },
      { language: Language.EN, label: 'Rental guaranties' },
      { language: Language.NL, label: 'Huurgaranties' },
      { language: Language.DE, label: 'Mietgarantien' },
    ],
  },
  {
    id: `DefaultAccountLabel38`,
    class: ACCOUNT_SUSPENSE_TOPCLASS,
    topClass: ACCOUNT_SUSPENSE_TOPCLASS,
    labels: [
      { language: Language.FR, label: "Compte d'attente" },
      { language: Language.EN, label: 'Suspense account' },
      { language: Language.NL, label: 'Wachtrekening' },
      { language: Language.DE, label: 'Wartekonto' },
    ],
  },
  {
    id: `DefaultAccountLabel39`,
    class: ACCOUNT_OWNERS_TOPCLASS,
    topClass: ACCOUNT_OWNERS_TOPCLASS,
    labels: [
      { language: Language.FR, label: 'Propriétaires' },
      { language: Language.EN, label: 'Owners' },
      { language: Language.NL, label: 'Eigenaren' },
      { language: Language.DE, label: 'Eigentümer' },
    ],
  },
  {
    id: `DefaultAccountLabel40`,
    class: ACCOUNT_OWNERS_TOPCLASS,
    topClass: ACCOUNT_OWNERS_TOPCLASS,
    labels: [
      { language: Language.FR, label: 'Propriétaires' },
      { language: Language.EN, label: 'Owners' },
      { language: Language.NL, label: 'Eigenaren' },
      { language: Language.DE, label: 'Eigentümer' },
    ],
    customUsage: AccountLabelCustomUsage.OWNER_REQUEST_FOR_FUNDS,
  },
  {
    id: `DefaultAccountLabel41`,
    class: ACCOUNT_PAYMENT_PROVIDER_TRANSIT_CLASS,
    topClass: ACCOUNT_PAYMENT_PROVIDER_TRANSIT_TOPCLASS,
    labels: [
      { language: Language.FR, label: 'Compte de transit prestataire de paiement' },
      { language: Language.EN, label: 'Payment provider transit account' },
      { language: Language.NL, label: 'Transitrekening betalingsprovider' },
      { language: Language.DE, label: 'Zahlungsanbieter Transitkonto' },
    ],
  },
  {
    id: `DefaultAccountLabel42`,
    class: ACCOUNT_PAYMENT_PROVIDER_FEE_CLASS,
    topClass: ACCOUNT_CHARGES_TOPCLASS,
    labels: [
      { language: Language.FR, label: 'Frais prestataire de paiement' },
      { language: Language.EN, label: 'Payment provider fees' },
      { language: Language.NL, label: 'Kosten betalingsprovider' },
      { language: Language.DE, label: 'Zahlungsanbieter Gebühren' },
    ],
  },
  {
    id: `DefaultAccountLabel54`,
    class: ACCOUNT_TURNOVER_LETTING_FEE_CLASS,
    topClass: ACCOUNT_TURNOVER_SERVICES_TOPCLASS,
    labels: [
      { language: Language.FR, label: 'Frais de location' },
      { language: Language.EN, label: 'Letting fee' },
      { language: Language.NL, label: 'Verhuurkosten' },
      { language: Language.DE, label: 'Vermietungsgebühr' },
    ],
  },
  {
    id: `DefaultAccountLabel55`,
    class: ACCOUNT_TURNOVER_RENT_GUARANTEE_INSURANCE_CLASS,
    topClass: ACCOUNT_TURNOVER_SERVICES_TOPCLASS,
    labels: [
      { language: Language.FR, label: 'Assurance garantie locative' },
      { language: Language.EN, label: 'Rent guarantee insurance' },
      { language: Language.NL, label: 'Huurwaarborgverzekering' },
      { language: Language.DE, label: 'Mietkautionsversicherung' },
    ],
  },
  {
    id: `DefaultAccountLabel56`,
    class: ACCOUNT_TURNOVER_MANAGEMENT_FEES_CLASS,
    topClass: ACCOUNT_TURNOVER_SERVICES_TOPCLASS,
    labels: [
      { language: Language.FR, label: 'Honoraires de gestion' },
      { language: Language.EN, label: 'Management fees' },
      { language: Language.NL, label: 'Beheervergoedingen' },
      { language: Language.DE, label: 'Verwaltungsgebühren' },
    ],
  },
  {
    id: `DefaultAccountLabel57`,
    class: ACCOUNT_TURNOVER_SERVICE_CLEANING_CLASS,
    topClass: ACCOUNT_TURNOVER_SERVICES_TOPCLASS,
    labels: [
      { language: Language.FR, label: 'Services de nettoyage' },
      { language: Language.EN, label: 'Cleaning services' },
      { language: Language.NL, label: 'Schoonmaakdiensten' },
      { language: Language.DE, label: 'Reinigungsdienstleistungen' },
    ],
  },
  {
    id: `DefaultAccountLabel58`,
    class: ACCOUNT_TURNOVER_SALES_COMMISSION_CLASS,
    topClass: ACCOUNT_TURNOVER_SERVICES_TOPCLASS,
    labels: [
      { language: Language.FR, label: 'Commission de vente' },
      { language: Language.EN, label: 'Sales commission' },
      { language: Language.NL, label: 'Verkoopcommissie' },
      { language: Language.DE, label: 'Verkaufsprovision' },
    ],
  },
  {
    id: `DefaultAccountLabel59`,
    class: ACCOUNT_TURNOVER_CUSTOM_LINE_CLASS,
    topClass: ACCOUNT_TURNOVER_SERVICES_TOPCLASS,
    labels: [
      { language: Language.FR, label: 'Autres prestations' },
      { language: Language.EN, label: 'Other services' },
      { language: Language.NL, label: 'Overige diensten' },
      { language: Language.DE, label: 'Sonstige Leistungen' },
    ],
  },
];

const DEFAULT_SUSPENSE_ACCOUNT_LABEL_IDS: Record<number, string> = {
  [ACCOUNT_SUSPENSE_RENTAL_GUARANTIES_RECEIVED_CLASS]: 'DefaultAccountLabel43',
  [ACCOUNT_SUSPENSE_TURNOVER_RENT_CLASS]: 'DefaultAccountLabel44',
  [ACCOUNT_SUSPENSE_TURNOVER_FURNISHED_RENT_CLASS]: 'DefaultAccountLabel45',
  [ACCOUNT_SUSPENSE_TURNOVER_FIXED_CHARGES_CLASS]: 'DefaultAccountLabel46',
  [ACCOUNT_SUSPENSE_TURNOVER_PROVISIONED_CHARGES_CLASS]: 'DefaultAccountLabel47',
  [ACCOUNT_SUSPENSE_TURNOVER_CHARGES_ADJUSTEMENT_CLASS]: 'DefaultAccountLabel48',
  [ACCOUNT_SUSPENSE_TURNOVER_VARIOUS_OPERATIONS_SERVICE_CLASS]: 'DefaultAccountLabel49',
  [ACCOUNT_SUSPENSE_TURNOVER_VARIOUS_OPERATIONS_CHARGE_CLASS]: 'DefaultAccountLabel50',
  [ACCOUNT_SUSPENSE_TURNOVER_TERMINATION_INDEMNITY]: 'DefaultAccountLabel51',
  [ACCOUNT_SUSPENSE_TURNOVER_EXCEPTIONAL_REVENUE_CLASS]: 'DefaultAccountLabel52',
};

const DEFAULT_SUSPENSE_ACCOUNT_LABELS: DefaultAccountLabel[] = Object.entries(DEFAULT_SUSPENSE_ACCOUNT_LABEL_IDS).map(
  ([suspenseAccountClass, id]) => {
    const mirroredAccountClass = getMirroredAccountClassFor(Number(suspenseAccountClass));
    const mirroredAccountLabel = DEFAULT_LEDGER_ACCOUNT_LABELS.find(
      (accountLabel) => accountLabel.class === mirroredAccountClass && isNil(accountLabel.customUsage)
    );
    return {
      id,
      class: Number(suspenseAccountClass),
      topClass: ACCOUNT_SUSPENSE_TOPCLASS,
      labels: mirroredAccountLabel?.labels ?? [],
    };
  }
);

export const DEFAULT_CHARGES_PAID_IN_THE_PAST_ACCOUNT_LABEL: DefaultAccountLabel = {
  id: `DefaultAccountLabel53`,
  class: ACCOUNT_SUSPENSE_CHARGES_PAID_IN_THE_PAST_CLASS,
  topClass: ACCOUNT_SUSPENSE_TOPCLASS,
  labels: [
    { language: Language.FR, label: 'Charges déjà payées avant reprise' },
    { language: Language.EN, label: 'Charges already paid before migration' },
    { language: Language.NL, label: 'Lasten al betaald voor overname' },
    { language: Language.DE, label: 'Vor Übernahme bereits bezahlte Kosten' },
  ],
};

export const DEFAULT_CHARGES_POSTING_ACCOUNT_LABELS: DefaultAccountLabel[] = [
  {
    id: `DefaultAccountLabel60`,
    class: ACCOUNT_ACP_FUNDS_TOPCLASS,
    topClass: ACCOUNT_ACP_FUNDS_TOPCLASS,
    labels: [
      { language: Language.FR, label: 'Créance sur ACP — fonds de roulement et de réserve' },
      { language: Language.EN, label: 'Receivable on the co-ownership — working capital and reserve funds' },
      { language: Language.NL, label: 'Vordering op de VME — werkkapitaal en reservefonds' },
      { language: Language.DE, label: 'Forderung gegen die Eigentümergemeinschaft — Betriebs- und Rücklagenfonds' },
    ],
  },
];

export const DEFAULT_HOA_WORKING_CAPITAL_ACCOUNT_LABEL: DefaultAccountLabel = {
  id: 'DefaultAccountLabel61',
  class: ACCOUNT_ACP_WORKING_CAPITAL_CLASS,
  topClass: ACCOUNT_CHARGES_TOPCLASS,
  labels: [
    { language: Language.FR, label: 'Fonds de roulement ACP — provisions pour charges' },
    { language: Language.EN, label: 'Co-ownership working capital — charge provisions' },
    { language: Language.NL, label: 'Werkkapitaal VME — provisies voor lasten' },
    { language: Language.DE, label: 'Betriebsfonds Eigentümergemeinschaft — Kostenvorschüsse' },
  ],
};

export const DEFAULT_ACCOUNT_LABELS: DefaultAccountLabel[] = [
  ...DEFAULT_LEDGER_ACCOUNT_LABELS,
  ...DEFAULT_SUSPENSE_ACCOUNT_LABELS,
  DEFAULT_CHARGES_PAID_IN_THE_PAST_ACCOUNT_LABEL,
  ...DEFAULT_CHARGES_POSTING_ACCOUNT_LABELS,
  DEFAULT_HOA_WORKING_CAPITAL_ACCOUNT_LABEL,
];

export const DEFAULT_DOCUMENT_CATEGORIES = [
  {
    id: 'DefaultDocumentCategory1',
    fileCategory: FileCategory.LEASE_CONTRACT,
    labels: [
      { language: 'fr', label: 'Contrat de bail' },
      { language: 'en', label: 'Lease contract' },
      { language: 'nl', label: 'Huurcontract' },
      { language: 'de', label: 'Mietvertrag' },
    ],
  },
  {
    id: 'DefaultDocumentCategory2',
    fileCategory: FileCategory.LEASE_CONTRACT_END,
    labels: [
      { language: 'fr', label: 'Fin de bail' },
      { language: 'en', label: 'End of lease' },
      { language: 'nl', label: 'Einde van huur' },
      { language: 'de', label: 'Mietende' },
    ],
  },
  {
    id: 'DefaultDocumentCategory3',
    fileCategory: FileCategory.LEASE_INVENTORY_CONTRACT,
    labels: [
      { language: 'fr', label: 'Etat des lieux' },
      { language: 'en', label: 'Inventory' },
      { language: 'nl', label: 'Inventaris' },
      { language: 'de', label: 'Bestandsaufnahme' },
    ],
  },
  {
    id: 'DefaultDocumentCategory4',
    fileCategory: FileCategory.CHARGE_INVOICE,
    labels: [
      { language: 'fr', label: 'Facture de charge' },
      { language: 'en', label: 'Charge invoice' },
      { language: 'nl', label: 'Lastenfactuur' },
      { language: 'de', label: 'Gebührenrechnung' },
    ],
  },
  {
    id: 'DefaultDocumentCategory5',
    fileCategory: FileCategory.INVOICE,
    labels: [
      { language: 'fr', label: 'Facture' },
      { language: 'en', label: 'Invoice' },
      { language: 'nl', label: 'Factuur' },
      { language: 'de', label: 'Rechnung' },
    ],
  },
  {
    id: 'DefaultDocumentCategory6',
    fileCategory: FileCategory.PEB,
    labels: [
      { language: 'fr', label: 'PEB' },
      { language: 'en', label: 'EPB' },
      { language: 'nl', label: 'EPC' },
      { language: 'de', label: 'EPB' }, // no acronyme found online Energieausweis was proposed
    ],
  },
  {
    id: 'DefaultDocumentCategory7',
    fileCategory: FileCategory.CO_OWNERSHIP_REGULATIONS,
    labels: [
      { language: 'fr', label: 'Règlement de copropriété' },
      { language: 'en', label: 'Co-ownership regulations' },
      { language: 'nl', label: 'Mede-eigendomsreglement' },
      { language: 'de', label: 'Miteigentümerordnung' },
    ],
  },
  {
    id: 'DefaultDocumentCategory8',
    fileCategory: FileCategory.RULES_OF_PROCEDURE,
    labels: [
      { language: 'fr', label: "Règlement d'ordre intérieur" },
      { language: 'en', label: 'Rules of procedure' },
      { language: 'nl', label: 'Huishoudelijk reglement' },
      { language: 'de', label: 'Geschäftsordnung' },
    ],
  },
  {
    id: 'DefaultDocumentCategory9',
    fileCategory: FileCategory.HYDROCARBONTANK_CERTIFICATE,
    labels: [
      { language: 'fr', label: 'Certificat de citerne à mazout' },
      { language: 'en', label: 'Oil tank certificate' },
      { language: 'nl', label: 'Mazouttankcertificaat' },
      { language: 'de', label: 'Heizöltankzertifikat' },
    ],
  },
  {
    id: 'DefaultDocumentCategory10',
    fileCategory: FileCategory.FURNITURE_INVENTORY,
    labels: [
      { language: 'fr', label: 'Inventaire du mobilier' },
      { language: 'en', label: 'Furniture inventory' },
      { language: 'nl', label: 'Meubilair inventaris' },
      { language: 'de', label: 'Möbelinventar' },
    ],
  },
  {
    id: 'DefaultDocumentCategory11',
    fileCategory: FileCategory.HEATING,
    labels: [
      { language: 'fr', label: 'Chauffage' },
      { language: 'en', label: 'Heating' },
      { language: 'nl', label: 'Verwarming' },
      { language: 'de', label: 'Heizung' },
    ],
  },
  {
    id: 'DefaultDocumentCategory12',
    fileCategory: FileCategory.CHIMNEY,
    labels: [
      { language: 'fr', label: 'Cheminée' },
      { language: 'en', label: 'Chimney' },
      { language: 'nl', label: 'Schoorsteen' },
      { language: 'de', label: 'Schornstein' },
    ],
  },
  {
    id: 'DefaultDocumentCategory13',
    fileCategory: FileCategory.UTILITY,
    labels: [
      { language: 'fr', label: 'Utilité' },
      { language: 'en', label: 'Utility' },
      { language: 'nl', label: 'Nutsbedrijf' },
      { language: 'de', label: 'Versorgungsleistung' },
    ],
  },
  {
    id: 'DefaultDocumentCategory14',
    fileCategory: FileCategory.DETECTOR,
    labels: [
      { language: 'fr', label: 'Détecteur' },
      { language: 'en', label: 'Detector' },
      { language: 'nl', label: 'Detector' },
      { language: 'de', label: 'Detektor' },
    ],
  },
  {
    id: 'DefaultDocumentCategory15',
    fileCategory: FileCategory.INVENTORY_OF_FIXTURES,
    labels: [
      { language: 'fr', label: 'Inventaire des installations' },
      { language: 'en', label: 'Inventory of fixtures' },
      { language: 'nl', label: 'Inventaris van armaturen' },
      { language: 'de', label: 'Bestandsaufnahme der Einrichtungen' },
    ],
  },
  {
    id: 'DefaultDocumentCategory16',
    fileCategory: FileCategory.COMMUNICATION,
    labels: [
      { language: 'fr', label: 'Communication' },
      { language: 'en', label: 'Communication' },
      { language: 'nl', label: 'Communicatie' },
      { language: 'de', label: 'Kommunikation' },
    ],
  },
  {
    id: 'DefaultDocumentCategory17',
    fileCategory: FileCategory.ADVERTISEMENT_PICTURE,
    labels: [
      { language: 'fr', label: 'Image publicitaire' },
      { language: 'en', label: 'Advertisement picture' },
      { language: 'nl', label: 'Advertentiebeeld' },
      { language: 'de', label: 'Werbebild' },
    ],
  },
  {
    id: 'DefaultDocumentCategory18',
    fileCategory: FileCategory.CLIENT_LOGO,
    labels: [
      { language: 'fr', label: 'Logo client' },
      { language: 'en', label: 'Client logo' },
      { language: 'nl', label: 'Klantlogo' },
      { language: 'de', label: 'Kundenlogo' },
    ],
  },
  {
    id: 'DefaultDocumentCategory19',
    fileCategory: FileCategory.BASIC_DEED,
    labels: [
      { language: 'fr', label: 'Acte de base' },
      { language: 'en', label: 'Basic deed' },
      { language: 'nl', label: 'Basisakte' },
      { language: 'de', label: 'Grundurkunde' },
    ],
  },
  {
    id: 'DefaultDocumentCategory21',
    fileCategory: FileCategory.STATEMENT_DETAIL,
    labels: [
      { language: 'fr', label: 'Détail du décompte' },
      { language: 'en', label: 'Statement detail' },
      { language: 'nl', label: 'Detail van de verklaring' },
      { language: 'de', label: 'Abrechnungsdetail' },
    ],
  },
  {
    id: 'DefaultDocumentCategory22',
    fileCategory: FileCategory.STATEMENT_RECEIPT,
    labels: [
      { language: 'fr', label: 'Reçu du décompte' },
      { language: 'en', label: 'Statement receipt' },
      { language: 'nl', label: 'Verklaringsontvangst' },
      { language: 'de', label: 'Abrechnungsbeleg' },
    ],
  },
  {
    id: 'DefaultDocumentCategory23',
    fileCategory: FileCategory.CONTRACT_AMENDMENT,
    labels: [
      { language: 'fr', label: 'Autres documents à signer' },
      { language: 'en', label: 'Other documents to sign' },
      { language: 'nl', label: 'Andere documenten om te ondertekenen' },
      { language: 'de', label: 'Andere zu unterzeichnende Dokumente' },
    ],
  },
  {
    id: 'DefaultDocumentCategory24',
    fileCategory: FileCategory.LEASE_PRICE_HISTORY_DETAIL,
    labels: [
      { language: 'fr', label: "Détail de l'historique des loyers" },
      { language: 'en', label: 'Lease price history detail' },
      { language: 'nl', label: 'Detail van de huurprijs geschiedenis' },
      { language: 'de', label: 'Detail des Mietpreisverlaufs' },
    ],
  },
  {
    id: 'DefaultDocumentCategory25',
    fileCategory: FileCategory.TICKET,
    labels: [
      { language: 'fr', label: 'Ticket' },
      { language: 'en', label: 'Ticket' },
      { language: 'nl', label: 'Ticket' },
      { language: 'de', label: 'Ticket' },
    ],
  },
  {
    id: 'DefaultDocumentCategory26',
    fileCategory: FileCategory.LETTER,
    labels: [
      { language: 'fr', label: 'Lettre' },
      { language: 'en', label: 'Letter' },
      { language: 'nl', label: 'Brief' },
      { language: 'de', label: 'Brief' },
    ],
  },
  {
    id: 'DefaultDocumentCategory27',
    fileCategory: FileCategory.INVOICE_RECEIPT,
    labels: [
      { language: 'fr', label: 'Quittance' },
      { language: 'en', label: 'Receipt' },
      { language: 'nl', label: 'Betalingsbewijs' },
      { language: 'de', label: 'Quittung' },
    ],
  },
  {
    id: 'DefaultDocumentCategory28',
    fileCategory: FileCategory.RENTAL_DEPOSIT,
    labels: [
      { language: 'fr', label: 'Garantie Locative' },
      { language: 'en', label: 'Rental deposit' },
      { language: 'nl', label: 'Huurwaarborg' },
      { language: 'de', label: 'Mietkautionen' },
    ],
  },
  {
    id: 'DefaultDocumentCategory29',
    fileCategory: FileCategory.CONTRACTUAL_INFORMATION,
    labels: [
      { language: 'en', label: 'Contractual Information' },
      { language: 'nl', label: 'Contractuele Informatie' },
      { language: 'fr', label: 'Informations Contractuelles' },
      { language: 'de', label: 'Vertragliche Informationen' },
    ],
  },
  {
    id: 'DefaultDocumentCategory30',
    fileCategory: FileCategory.INTERNAL_COMMENT,
    labels: [
      { language: 'en', label: 'Internal comments' },
      { language: 'nl', label: 'Interne opmerkingen' },
      { language: 'fr', label: 'Commentaires internes' },
      { language: 'de', label: 'Interne Kommentare' },
    ],
  },
];

export const getDefaultDocumentCategory = (fileCategory: FileCategory) =>
  DEFAULT_DOCUMENT_CATEGORIES.find((documentCategory) => documentCategory.fileCategory === fileCategory);
